import { FC, Fragment } from 'react';
import Badges from '@/components/UI/badges/Badges';
import Box from '@/components/UI/box/Box';
import Typography from '@/components/UI/typography/Typography';
import Styles from './HouseFeaturesCardItem.module.scss';
import Image from 'next/image';
import React from 'react';
import Alert from '@/components/UI/alert/Alert';
const categoryDic = {
  accommodation_services: 'خدمات اقامتگاه',
  amenities: 'امکانات رفاهی',
  cctv_camera: 'دوربین مدار بسته',
  cooling_system: 'سیستم سرمایشی',
  health_facilities: 'پک بهداشتی یکبار مصرف',
  heating_system: 'سیستم گرمایشی',
  kitchen_facilities: 'امکانات آشپزخانه',
  safety_facilities: 'امکانات ایمنی',
  spaces: 'فضاهای اقامتگاه',
  main: 'امکانات اصلی',
  special_facilities: 'امکانات ویژه'
};
const seasons = {
  spring: 'بهار',
  summer: 'تابستان',
  autumn: 'پاییز',
  winter: 'زمستان'
};
type Props = {
  header: string;
  items: any;
};
const isShared = item => {
  return !!(item.is_share && item.is_share === 1);
};
const generatePoolDescription = item => {
  const depth = item.depth ? `عمق ${item.depth} متر ` : '';
  const length = item.length ? `طول ${item.length} متر ` : '';
  const width = item.width ? `عرض ${item.width} متر ` : '';
  const hasSessionUsage = item.has_session_usage ? `قابل استفاده به صورت سانسی و اشتراکی` : '';
  let warmAndSystem = '';
  if (item.has_purification_system === 1 && item.has_warm_water === 1) {
    warmAndSystem = 'مجهز به آب گرم و سیستم تصفیه';
  } else if (item.has_purification_system === 1 && item.has_warm_water === 0) {
    warmAndSystem = 'مجهز به سیستم تصفیه';
  } else if (item.has_purification_system === 0 && item.has_warm_water === 1) {
    warmAndSystem = 'مجهز به  آب گرم';
  } else if (item.has_purification_system === 0 && item.has_warm_water === 0) {
    warmAndSystem = 'مجهز به  آب گرم و سیستم تصفیه نیست';
  }
  return `${depth}  ${width}  ${length}\n${warmAndSystem}\n${hasSessionUsage}`;
};
const generateParkingDescription = item => {
  const withCeiling = item.meta_data?.without_ceiling?.capacity ? `${item.meta_data?.without_ceiling?.capacity} پارکینگ رو باز` : '';
  const withoutCeiling = item.meta_data?.with_ceiling?.capacity ? `${item.meta_data?.with_ceiling?.capacity} پارکینگ سرپوشیده` : '';
  return `${withCeiling} ${withCeiling && withoutCeiling ? '،' : ''} ${withoutCeiling} `;
};
const generateYardDescription = item => {
  const area = item.meta_data?.area;
  return area > 0 ? `متراژ ${area} متر` : '';
};
const generateFoodServing = item => {
  const count = item.meta_data?.count;
  return ` ${count}  پکیج سرو غذا شامل بشقاب، قاشق و چنگال و لیوان`;
};
const generateSecurity = item => {
  const options = {
    inside: 'نگهبان در اقامتگاه(حیاط) مستقر است.',
    outside: 'نگهبان در خارج اقامتگاه مستقر است.',
    dont_have: 'اقامتگاه نگهبان ندارد.'
  };
  return options[item?.meta_data?.placed] ?? '';
};
const generateDescription = item => {
  let description = '';
  if (item.feature_name === 'yard') {
    description = generateYardDescription(item);
  } else if (item.feature_name === 'parking') {
    description = generateParkingDescription(item);
  } else if (item.type === 'indoor' || item.type === 'outdoor') {
    description = generatePoolDescription(item);
  } else if (item.feature_name === 'security_guard/janitor') {
    description = generateSecurity(item);
  } else if (item.feature_name === 'food_serving_equipment') {
    description = generateFoodServing(item);
  }
  return description;
};
const generateSeasonText = seasonsArr => {
  let text = '';
  if (seasonsArr?.length === 4) {
    text = 'قابل استفاده در تمامی فصل‌های سال';
  } else {
    text = 'قابل استفاده در فصل ';
    seasonsArr?.map((season, index) => {
      text += ` ${seasons[season]} `;
      if (index !== seasonsArr?.length - 1) {
        text += ' و ';
      }
    });
  }
  return text;
};
const meals = ['breakfast', 'lunch', 'dinner'];
const HouseFeaturesCardItem: FC<Props> = ({
  header,
  items
}) => {
  return <Box data-sentry-element="Box" data-sentry-component="HouseFeaturesCardItem" data-sentry-source-file="HouseFeaturesCardItem.tsx">
      <Typography variant="bodyL_Bold" className={Styles['header']} component="h3" data-sentry-element="Typography" data-sentry-source-file="HouseFeaturesCardItem.tsx">
        {categoryDic[header]}
      </Typography>
      {items.map((item, index) => item.feature_name !== 'pool' && item.feature_name !== 'experience' ? <Box key={`FEATURE_ITEM_${index}`} className={Styles['item']}>
            <Box className={Styles['top-content']}>
              <Box className={Styles['right-section']}>
                <Image src={item.icon_url} alt={item.icon_url} width={19} height={19} />
                <Typography variant="bodyL"> {item.feature_label}</Typography>
              </Box>
              <Box className={Styles['left-section']}>
                {isShared(item) && <Badges label="اشتراکی" type="default" />}
                {meals.includes(item.feature_name) && <Badges label={item.meta_data?.is_free ? 'رایگان' : 'هزینه دارد'} type="default" />}
              </Box>
            </Box>
            <Box>
              <Typography variant="bodyM" color="secondary_main" className={Styles['description']}>
                {generateDescription(item)}
              </Typography>
            </Box>
          </Box> : item.feature_name === 'experience' ? item.meta_data?.map(exp => <>
              <Box key={`FEATURE_ITEM_${index}`} className={Styles['item']}>
                <Box className={Styles['top-content']}>
                  <Box className={Styles['right-section']}>
                    <Image src={item.icon_url} alt={item.icon_url} width={19} height={19} />
                    <Typography variant="bodyL"> {exp?.title}</Typography>
                  </Box>
                  <Box className={Styles['left-section']}>
                    {isShared(item) && <Badges label="اشتراکی" type="default" />}
                    <Badges label={exp?.price > 0 ? 'هزینه دارد' : 'رایگان'} type="default" />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="bodyM" color="secondary_main" className={Styles['description']}>
                    {exp?.description}
                  </Typography>
                </Box>
              </Box>
            </>) : <Fragment key={`HAS_DETAIL_ITEM_${index}`}>
            {item.meta_data.map((pool, index) => <Box key={`POOL_${index}`} className={Styles['item']}>
                <Box className={Styles['top-content']}>
                  <Box className={Styles['right-section']}>
                    <Image src={item.icon_url} alt={item.icon_url} width={24} height={24} />
                    <Typography variant="bodyL">
                      {pool.type === 'indoor' ? 'استخر سرپوشیده' : 'استخر روباز'}
                    </Typography>
                  </Box>
                  <Box className={Styles['left-section']}>
                    {pool.is_active === 1 && <Badges label="فعال" type="success" />}
                    {pool.is_active === 0 && <Badges label="غیرفعال" type="error" />}
                    {isShared(pool) && <Badges label="اشتراکی" type="default" />}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="bodyM" color="secondary_main" className={Styles['description']}>
                    {generateDescription(pool)}
                  </Typography>
                </Box>
                <Box className={Styles['alert']}>
                  {pool?.seasons?.length > 0 && <Alert title={generateSeasonText(pool?.seasons)} type="warning" icon="info-circle" />}
                </Box>
              </Box>)}
          </Fragment>)}
    </Box>;
};
export default HouseFeaturesCardItem;